import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaUsers,
  FaUserTie,
  FaHourglassHalf,
  FaCalendarAlt,
  FaCheckCircle,
  FaFlagCheckered,
  FaTimesCircle,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";

const emptySplit = { auto: 0, manual: 0 };
const emptyStats = {
  totalRidesAssigned: 0,
  totalRidesWorked: 0,
  requested: {
    total: 0,
    accepted: { ...emptySplit },
    completed: { ...emptySplit },
    cancelled: { ...emptySplit },
  },
  planned: {
    total: 0,
    accepted: { ...emptySplit },
    completed: { ...emptySplit },
    cancelled: { ...emptySplit },
  },
};

const SKIP_FILTER_KEYS = new Set([
  "page",
  "limit",
  "sortBy",
  "sortOrder",
  // Keep cards meaningful after selecting a status card.
  "status",
  // Card clicks apply Now/Scheduled for list filtering; keep stats un-sliced.
  "scheduleType",
]);

const buildQueryString = (filters) => {
  const params = new URLSearchParams();
  if (!filters || typeof filters !== "object") return params.toString();
  Object.entries(filters).forEach(([key, value]) => {
    if (
      SKIP_FILTER_KEYS.has(key) ||
      value === undefined ||
      value === null ||
      typeof value !== "string"
    ) {
      return;
    }
    const trimmed = value.trim();
    if (trimmed) params.append(key, trimmed);
  });
  return params.toString();
};

const getValueByPath = (obj, path) =>
  path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

const row1 = [
  {
    key: "totalRidesAssigned",
    kind: "single",
    label: "Total Rides Assigned",
    gradient: "bg-gradient-to-r from-violet-500 to-violet-600",
    icon: FaUsers,
  },
  {
    key: "requested.total",
    kind: "single",
    label: "Requested Assigned",
    gradient: "bg-gradient-to-r from-amber-500 to-amber-600",
    icon: FaHourglassHalf,
  },
  {
    key: "requested.accepted",
    kind: "split",
    label: "Accepted",
    gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    icon: FaCheckCircle,
  },
  {
    key: "requested.completed",
    kind: "split",
    label: "Completed",
    gradient: "bg-gradient-to-r from-green-500 to-green-600",
    icon: FaFlagCheckered,
  },
  {
    key: "requested.cancelled",
    kind: "split",
    label: "Cancelled",
    gradient: "bg-gradient-to-r from-rose-500 to-rose-600",
    icon: FaTimesCircle,
  },
];

const row2 = [
  {
    key: "totalRidesWorked",
    kind: "single",
    label: "Total Rides Worked",
    gradient: "bg-gradient-to-r from-fuchsia-500 to-fuchsia-600",
    icon: FaUserTie,
  },
  {
    key: "planned.total",
    kind: "single",
    label: "Planned Assigned",
    gradient: "bg-gradient-to-r from-indigo-500 to-indigo-600",
    icon: FaCalendarAlt,
  },
  {
    key: "planned.accepted",
    kind: "split",
    label: "Accepted",
    gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    icon: FaCheckCircle,
  },
  {
    key: "planned.completed",
    kind: "split",
    label: "Completed",
    gradient: "bg-gradient-to-r from-green-500 to-green-600",
    icon: FaFlagCheckered,
  },
  {
    key: "planned.cancelled",
    kind: "split",
    label: "Cancelled",
    gradient: "bg-gradient-to-r from-rose-500 to-rose-600",
    icon: FaTimesCircle,
  },
];

const cardBaseClass =
  "rounded-xl p-4 shadow-md text-white transition-transform duration-150";

const filterByKey = {
  totalRidesAssigned: { status: "", scheduleType: "" },
  // requested.* bucket = scheduledAt === null -> scheduleType "Now"
  "requested.total": { status: "", scheduleType: "Now" },
  "requested.accepted": { status: "Accepted", scheduleType: "Now" },
  "requested.completed": { status: "Completed", scheduleType: "Now" },
  "requested.cancelled": { status: "Cancelled", scheduleType: "Now" },
  // planned.* bucket = scheduledAt !== null -> scheduleType "Scheduled"
  "planned.total": { status: "", scheduleType: "Scheduled" },
  "planned.accepted": { status: "Accepted", scheduleType: "Scheduled" },
  "planned.completed": { status: "Completed", scheduleType: "Scheduled" },
  "planned.cancelled": { status: "Cancelled", scheduleType: "Scheduled" },
};

const buildCardClasses = (cfg, canClick, isActive) =>
  `${cardBaseClass} ${cfg.gradient} ${
    canClick
      ? "cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70"
      : ""
  } ${isActive ? "ring-4 ring-offset-2 ring-offset-white ring-slate-800 scale-[1.02]" : ""}`;

const SingleCard = ({ cfg, value, loading, onClick, isActive }) => {
  const Icon = cfg.icon;
  const nextFilter = filterByKey[cfg.key];
  const canClick = typeof onClick === "function" && Boolean(nextFilter);
  const handleCardClick = () => {
    if (!canClick) return;
    onClick(nextFilter);
  };
  return (
    <button
      type="button"
      className={buildCardClasses(cfg, canClick, isActive)}
      title={cfg.label}
      onClick={handleCardClick}
      disabled={!canClick}
      aria-label={`Filter rides by ${cfg.label}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <span className="text-xs uppercase tracking-wide opacity-90 leading-snug break-words">
          {cfg.label}
        </span>
        <Icon className="text-lg opacity-80 flex-shrink-0" />
      </div>
      <div className="text-3xl font-bold leading-tight">
        {loading ? "—" : value ?? 0}
      </div>
    </button>
  );
};

const SplitCard = ({ cfg, value, loading, onClick, isActive }) => {
  const Icon = cfg.icon;
  const safe = value || emptySplit;
  const nextFilter = filterByKey[cfg.key];
  const canClick = typeof onClick === "function" && Boolean(nextFilter);
  const handleCardClick = () => {
    if (!canClick) return;
    onClick(nextFilter);
  };
  return (
    <button
      type="button"
      className={buildCardClasses(cfg, canClick, isActive)}
      title={cfg.label}
      onClick={handleCardClick}
      disabled={!canClick}
      aria-label={`Filter rides by ${cfg.label}`}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <span className="text-xs uppercase tracking-wide opacity-90 leading-snug break-words">
          {cfg.label}
        </span>
        <Icon className="text-lg opacity-80 flex-shrink-0" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white/15 rounded-lg p-2">
          <div className="text-[10px] tracking-wider opacity-90">
            Automatically
          </div>
          <div className="text-2xl font-bold leading-tight">
            {loading ? "—" : safe.auto ?? 0}
          </div>
        </div>
        <div className="bg-white/15 rounded-lg p-2">
          <div className="text-[10px] tracking-wider opacity-90">
            Manually
          </div>
          <div className="text-2xl font-bold leading-tight">
            {loading ? "—" : safe.manual ?? 0}
          </div>
        </div>
      </div>
    </button>
  );
};

const isCardActive = (cfgKey, selectedStatus, selectedScheduleType) => {
  const expected = filterByKey[cfgKey];
  if (!expected) return false;
  const expectedStatus = (expected.status || "").toLowerCase();
  const expectedScheduleType = (expected.scheduleType || "").toLowerCase();
  return (
    expectedStatus === selectedStatus && expectedScheduleType === selectedScheduleType
  );
};

const renderCard = (
  cfg,
  stats,
  loading,
  onCardFilter,
  selectedStatus,
  selectedScheduleType
) => {
  const value = getValueByPath(stats, cfg.key);
  const isActive = isCardActive(cfg.key, selectedStatus, selectedScheduleType);
  if (cfg.kind === "split") {
    return (
      <SplitCard
        key={cfg.key}
        cfg={cfg}
        value={value}
        loading={loading}
        onClick={onCardFilter}
        isActive={isActive}
      />
    );
  }
  return (
    <SingleCard
      key={cfg.key}
      cfg={cfg}
      value={value}
      loading={loading}
      onClick={onCardFilter}
      isActive={isActive}
    />
  );
};

const RidesAssignedEmployeeStatsPanel = ({ filters, onCardFilter }) => {
  const [stats, setStats] = useState(emptyStats);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const assignedEmployeeId = (filters?.assignedEmployeeId || "").trim();
  const queryString = useMemo(() => buildQueryString(filters), [filters]);
  const selectedStatus = (filters?.status || "").trim().toLowerCase();
  const selectedScheduleType = (filters?.scheduleType || "").trim().toLowerCase();

  useEffect(() => {
    if (!assignedEmployeeId) {
      if (abortRef.current) abortRef.current.abort();
      setStats(emptyStats);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      const url = queryString
        ? `${API.ADMIN.RIDES_EMPLOYEE_STATS}?${queryString}`
        : API.ADMIN.RIDES_EMPLOYEE_STATS;

      axiosInstance
        .get(url, { signal: controller.signal })
        .then((res) => {
          const data = res.data?.data;
          if (!data) {
            setStats(emptyStats);
            return;
          }
          setStats({
            ...emptyStats,
            ...data,
            requested: { ...emptyStats.requested, ...(data.requested || {}) },
            planned: { ...emptyStats.planned, ...(data.planned || {}) },
          });
        })
        .catch((err) => {
          if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
          setError("Failed to load assigned employee stats");
        })
        .finally(() => {
          if (abortRef.current === controller) {
            setLoading(false);
          }
        });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [assignedEmployeeId, queryString]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  if (!assignedEmployeeId) return null;

  return (
    <div className="bg-white rounded-lg shadow mb-6 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Assigned Employee Stats
        </h3>
        {error && (
          <span className="text-xs text-red-500" role="alert">
            {error}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
        {row1.map((cfg) =>
          renderCard(
            cfg,
            stats,
            loading,
            onCardFilter,
            selectedStatus,
            selectedScheduleType
          )
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {row2.map((cfg) =>
          renderCard(
            cfg,
            stats,
            loading,
            onCardFilter,
            selectedStatus,
            selectedScheduleType
          )
        )}
      </div>
    </div>
  );
};

export default RidesAssignedEmployeeStatsPanel;
